<template>
  <Dialog
    :is-open="isOpen"
    title="Task Took Longer Than Expected"
    width="450px"
    @close="closeDialog"
  >
    <div :class="$style.content">
      <p :class="$style.message">
        "{{ taskName }}" took {{ formatDuration(actualDuration) }} instead of
        the estimated {{ formatDuration(estimatedDuration) }}.
      </p>
      <p :class="$style.question">What do you think caused this?</p>

      <div :class="$style.reasonsSection">
        <div :class="$style.reasonChips">
          <button
            v-for="reason in reasons"
            :key="reason.id"
            :class="[
              $style.reasonChip,
              { [$style.selected]: selectedReasons.includes(reason.id) },
            ]"
            @click="toggleReason(reason.id)"
          >
            {{ reason.label }}
          </button>
        </div>
      </div>
    </div>

    <div :class="$style.actions">
      <button :class="$style.skipButton" @click="skipDialog">Skip</button>
      <button :class="$style.saveButton" @click="saveReasons">Save</button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import Dialog from "./Dialog.vue";
import { overrunReasonService } from "../services/overrunReasonService";

const props = defineProps<{
  isOpen: boolean;
  taskName: string;
  estimatedDuration: number;
  actualDuration: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save", reasons: string[]): void;
  (e: "skip"): void;
}>();

const reasons = overrunReasonService.getReasons();

const selectedReasons = ref<string[]>([]);

function formatDuration(ms: number): string {
  if (!ms) return "0h 0m";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
}

function toggleReason(reasonId: string) {
  const index = selectedReasons.value.indexOf(reasonId);
  if (index === -1) {
    selectedReasons.value.push(reasonId);
  } else {
    selectedReasons.value.splice(index, 1);
  }
}

function closeDialog() {
  emit("close");
}

function skipDialog() {
  emit("skip");
}

function saveReasons() {
  emit("save", selectedReasons.value);
}

// Reset selected reasons when dialog opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedReasons.value = [];
    }
  }
);
</script>

<style module>
.content {
  padding: 0 20px;
}

.message {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.question {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
}

.reasonsSection {
  margin-bottom: 20px;
}

.reasonChips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reasonChip {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
}

.reasonChip:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.reasonChip.selected {
  border-color: var(--primary-color);
  background: rgba(66, 184, 131, 0.2);
  color: var(--primary-color);
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 20px;
}

.skipButton,
.saveButton {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
}

.skipButton {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.skipButton:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.saveButton {
  background: var(--primary-color);
  color: white;
}

.saveButton:hover:not(:disabled) {
  background: var(--primary-hover);
}

.saveButton:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .content {
    padding: 0 15px;
  }

  .actions {
    padding: 15px;
  }

  .reasonChip {
    padding: 6px 10px;
    font-size: 12px;
  }
}
</style>
